/* Stickers Souq — custom sticker sheet builder (Shopify section).

   A custom sheet has one sticker size for all its stickers (15 Oct): S, M or L, the printed length of each
   sticker's longest side (each keeps its own shape; task 2 of the October brief). The size sets how many the
   sheet takes — the section's settings, A4 50 / 40 / 24 and A3 80 / 60 / 40 to start — drawn as an even grid
   of that many spaces, each sticker centred in its space at its real size. One flat price per sheet, whatever
   the size.

   "Add to cart" adds the Custom Sticker Sheet product (A4 or A3 variant) with the chosen
   stickers attached as line-item properties, so the order shows exactly what to print. It
   uses the theme's own cart drawer when there is one, otherwise it goes to /cart.

   Words come from window.ND (snippets/nd-head.liquid, locales nd.js.*) so the builder speaks the
   page's language; sticker names and the order details ("Sheet", "Stickers") stay in English — they
   are what the shop prints from. Stickers the shop adds in Content → Metaobjects arrive as
   [data-extra-stickers] with ids "m<id>"; their picture's address goes into the order too.

   Big stickers (task 4): a third card, "Big sticker", switches the library to the designs allowed big
   (catalogue "b": 1); a design opens a size panel (nd-big.js draws it on a laptop and a suitcase) that adds
   the "Big Sticker" product at that size with the design as line-item properties. */
(() => {
  const SHEETS = {
    A4: { w: 210, h: 297, caps: { S: 50, M: 40, L: 24 } },    // caps: stickers per sheet at each size, when the
    A3: { w: 297, h: 420, caps: { S: 80, M: 60, L: 40 } },    // section's settings don't say
  };
  const MARGIN = 10;                     // printable margin, mm
  const GAP = 4;                         // space between stickers, mm

  // Library tiles sit on a coloured card so each sticker's white die-cut border shows (the
  // Sticker Planet "souq" palette: mid-tones only, no white). Display only: on the sheet the
  // stickers stay on white paper. The colour comes from the sticker id, so it never shifts.
  const TILE_COLOURS = ["#7FB3C8", "#E4C98A", "#8FB59B", "#D98E86", "#8E93C4",
                        "#4E5D6B", "#C9A227", "#B5836F", "#6E9E8E", "#D2A0B4"];
  const num = id => typeof id === "number" ? id : (parseInt(String(id).replace(/\D/g, "").slice(-6), 10) || 0);
  const tileColour = id => { const n = num(id); return TILE_COLOURS[(n * 7 + Math.floor(n / 10) * 3) % TILE_COLOURS.length]; };
  const ND = window.ND || {}, T = ND.t || {};
  const f = (s, o) => (ND.f ? ND.f(s, o) : String(s).replace(/\{(\w+)\}/g, (m, k) => (o[k] != null ? o[k] : m)));
  const NTDc = () => window.NTD || {};
  // a category's name: as the shop wrote it in the Sticker Manager app (NTD.secNames, both languages), else the
  // theme's own translation
  const secName = s => s === "__new" ? (NTDc().newWord || "New") : ((NTDc().cats || []).length && NTDc().secNames && NTDc().secNames[s]) || (ND.secName ? ND.secName(s) : s);
  // "New": a sticker added in the Sticker Manager app shows the label for NTD.newDays days (d = the day it was added)
  const isNewS = s => !!(s.d && +(NTDc().newDays || 0) > 0 && (Date.now() - Date.parse(s.d)) / 864e5 <= +NTDc().newDays);
  const words = w => (ND.matchWords ? ND.matchWords(w) : [w]);
  const idOf = v => (/^m/.test(String(v)) ? String(v) : +v);       // library ids are numbers, the shop's own "m…"
  const RTL = document.documentElement.dir === "rtl";

  const esc = t => String(t).replace(/[&<>"']/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const cm = mm => (mm / 10).toFixed(1).replace(/\.0$/, "");
  const SIZES = ["S", "M", "L"];

  function money(cents, format) {
    if (cents == null) return "";
    const n = cents / 100;
    // whole amounts without decimals ("QAR 15"), as money_without_trailing_zeros prints them
    const two = n % 1 ? n.toFixed(2) : String(n), none = String(Math.round(n));
    if (ND.price) return ND.price(n % 1 ? two : none);
    const fm = format || "{{amount}}";
    return fm.replace(/\{\{\s*amount_no_decimals\s*\}\}/, none)
            .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, two.replace(".", ","))
            .replace(/\{\{\s*amount\s*\}\}/, two)
            .replace(/<[^>]+>/g, "");
  }

  document.querySelectorAll("[data-ssb]").forEach(init);

  async function init(root) {
    const data = JSON.parse(root.querySelector("[data-ssb-json]").textContent);
    // The full library is a separate JSON asset ({i, n, s, r, a, m}), fetched once. Images live in
    // Shopify Files (a = 0) or in the theme's assets (a = 1), both named cs-<id>.webp.
    // m = the smallest size a sticker may print at ("M" for the Hassan & Rayyan and Fattoum
    // stickers); it rides along for the size choice (task 2 of the October brief).
    let STICKERS = data.stickers || [];
    if (data.list_url) {
      try {
        const list = await (await fetch(data.list_url)).json();
        // b = 1: the design may also be printed as a big sticker (task 4: Laptop & Travel); bmax = its largest size ("19x27")
        STICKERS = list.map(s => ({ id: s.i, n: s.n, s: s.s, r: s.r, m: s.m || "", b: s.b ? 1 : 0, bmax: s.bmax || "",
          u: s.u || (s.a ? data.assets_base : data.files_base) + "cs-" + s.i + ".webp" }));
      } catch (err) {
        root.querySelector("[data-count]").textContent = T.load_failed || "Stickers didn't load — please refresh the page.";
      }
    }
    try {
      // the shop's own stickers: page 1 (250) is in the page, the rest come 250 at a time from the data section
      const tag = root.querySelector("[data-extra-stickers]");
      let extra = JSON.parse((tag || {}).textContent || "[]");
      const pages = +((tag && tag.dataset.pages) || 1);
      // Shopify names the page parameter itself ("page_8e686d89=2"): take it from the next-page link
      const pageKey = ((tag && tag.dataset.next || "").match(/[?&](page[^=&]*)=/) || [])[1] || "page";
      if (pages > 1) {
        const more = await Promise.all(Array.from({ length: pages - 1 }, (_, k) => {
          const u = new URL(location.href); u.search = ""; u.searchParams.set("section_id", "nd-extras-json"); u.searchParams.set(pageKey, k + 2);
          return fetch(u).then(r => r.text()).then(h => {
            const t = new DOMParser().parseFromString(h, "text/html").querySelector("[data-extra-stickers]");
            return t ? JSON.parse(t.textContent || "[]") : [];
          }).catch(() => []);
        }));
        extra = extra.concat(...more);
      }
      // an entry with a sticker number (o) changes that library sticker (whatever it fills in: picture, name,
      // section, big, search words) or takes it out (h); an entry without one is a new sticker
      // a number that isn't in the library: a new sticker that got its number from the Sticker Manager app
      const libIds = new Set(STICKERS.map(s => String(s.id)));
      const numbered = extra.filter(x => x.o != null && !libIds.has(String(x.o)) && !x.h && x.n && x.u);
      const edits = new Map(extra.filter(x => x.o != null && libIds.has(String(x.o))).map(x => [String(x.o), x]));
      if (edits.size) STICKERS = STICKERS.flatMap(s => {
        const x = edits.get(String(s.id));
        if (!x) return [s];
        if (x.h) return [];
        const c = { ...s };
        if (x.n) c.n = x.n;
        if (x.s) c.s = x.s;
        if (x.u) { c.u = x.u; c.r = x.r || c.r; c.own = true; }   // a new picture goes into the order with its address
        if (x.b != null) c.b = x.b ? 1 : 0;
        if (x.k) c.k = x.k;
        if (x.m != null) c.m = x.m === "S" ? "" : x.m;            // smallest print size ("S" = any size)
        return [c];
      });
      STICKERS = STICKERS.concat(numbered.map(s => ({ id: s.o, n: s.n, s: s.s, r: s.r || 1, u: s.u, b: s.b ? 1 : 0,
        m: s.m && s.m !== "S" ? s.m : "", k: s.k || "", d: s.d || "", own: true })));
      STICKERS = STICKERS.concat(extra.filter(x => x.o == null && x.n && x.u)
        .map(s => ({ id: String(s.i), n: s.n, s: s.s, r: s.r || 1, u: s.u, b: s.b ? 1 : 0, k: s.k || "", d: s.d || "", own: true })));
    } catch (err) { /* no extra stickers */ }
    if (NTDc().applyCats) STICKERS = NTDc().applyCats(STICKERS);
    const hidden = new Set(String(data.hidden || "").split(/[\s,]+/).filter(Boolean).map(idOf));
    if (hidden.size) STICKERS = STICKERS.filter(s => !hidden.has(s.id));
    const BY_ID = new Map(STICKERS.map(s => [s.id, s]));
    // ?numbers=1: the sticker numbers under the names (for the shop: the number to put in a "Builder sticker" entry)
    const STAFF = /[?&]numbers=1(&|$)/.test(location.search);
    const $ = sel => root.querySelector(sel);
    const grid = $("[data-grid]"), paper = $("[data-paper]");

    let sheet = "A4", section = "", query = "", picks = [], uidSeq = 1;

    // "Big sticker" (task 4 of the October brief): a third card next to A4 and A3. The library then shows only
    // the designs allowed big (b: 1); a tap opens a panel with the Big Sticker product's sizes drawn on a laptop
    // and a suitcase (nd-big.js), and each design goes to the cart on its own line (Design + _Design ID).
    let mode = "sheet";
    const BIG = ((data.big && data.big.variants) || [])
      .map(v => ({ ...v, size: window.NDBig ? NDBig.parse(v.title) : null })).filter(v => v.size);
    const unesc = s => { const x = document.createElement("textarea"); x.innerHTML = s; return x.value; };   // translations arrive HTML-escaped
    const BIG_T = {};
    Object.keys((data.big && data.big.t) || {}).forEach(k => { BIG_T[k] = unesc(data.big.t[k]); });

    /* ---------- the sheet's sticker size: S · M · L ----------
       A size is the printed length of a sticker's longest side (the other side follows its shape r):
       the section's settings in mm, 15 / 25 / 35 by default (the print team may change them). One size for
       the whole sheet — the switch on the desk, the section's "Size the builder opens with" to start. A
       sticker's m is the smallest size it comes in ("M" for Hassan & Rayyan and Fattoum): on a smaller sheet
       its tile is greyed out and a tap says why. */
    const SIZE_MM = {};
    SIZES.forEach((z, k) => { SIZE_MM[z] = +(data.sizes && data.sizes[z]) || [15, 25, 35][k]; });
    const rank = z => SIZES.indexOf(z);
    const minOf = st => (st && SIZES.includes(st.m) ? st.m : "S");
    const zName = z => (data.names && data.names[z]) || z;                     // Small · Medium · Large, in the page's language
    let size = SIZES.includes(data.size) ? data.size : "L";
    const fits = (st, z = size) => rank(z) >= rank(minOf(st));                  // the sticker comes in that size
    const minNote = st => f(minOf(st) === "L" ? (T.min_l || "{section} stickers come in L only — choose L for this sheet to add them.")
                                              : (T.min_m || "{section} stickers come in M or L — choose M or L for this sheet to add them."),
                            { section: secName(st.s) });

    /* How many stickers a sheet takes at a size: the section's settings (A4 50 / 40 / 24, A3 80 / 60 / 40 to start),
       laid out as an even grid across the printable area. Of the grids whose every space holds a sticker of that
       size, the one with the fewest empty spaces at the end, then the one with the biggest spaces (A4: 4 × 6,
       5 × 8, 5 × 10; A3: 5 × 8, 6 × 10, 8 × 10). A number too big for the size → as many as fit. */
    const GRIDS = {};
    function gridOf(k = sheet, z = size) {
      if (GRIDS[k + z]) return GRIDS[k + z];
      const S = SHEETS[k], W = S.w - 2 * MARGIN, H = S.h - 2 * MARGIN, need = SIZE_MM[z];
      const want = Math.max(1, Math.round(+(data.caps && data.caps[k] && data.caps[k][z]) || S.caps[z]));
      let best = null;
      for (let cols = 1; cols <= want; cols++) {
        const rows = Math.ceil(want / cols);
        const side = Math.min((W - (cols - 1) * GAP) / cols, (H - (rows - 1) * GAP) / rows);
        if (side < need) continue;
        const empty = cols * rows - want;
        if (!best || empty < best.empty || (empty === best.empty && side > best.side)) best = { cols, rows, n: want, empty, side };
      }
      if (!best) {
        const cols = Math.max(1, Math.floor((W + GAP) / (need + GAP))), rows = Math.max(1, Math.floor((H + GAP) / (need + GAP)));
        best = { cols, rows, n: cols * rows };
      }
      return (GRIDS[k + z] = best);
    }
    const maxOf = (k = sheet, z = size) => gridOf(k, z).n;
    // " A smaller size fits more." — when one does, on this sheet
    const smallerHint = () => SIZES.slice(0, rank(size)).some(z => maxOf(sheet, z) > maxOf())
      ? " " + (T.or_smaller || "A smaller size fits more.") : "";

    const zseg = $("[data-zsize]");
    function setSize(z) {
      size = z;
      zseg.querySelectorAll("[data-z]").forEach(c => c.setAttribute("aria-pressed", c.dataset.z === z));
      grid.querySelectorAll(".ssb-tile").forEach(offTile);
    }
    zseg.addEventListener("click", e => {
      const b = e.target.closest("[data-z]"); if (!b || b.dataset.z === size) return;
      // stickers on the sheet that don't come in the new size stay: the customer takes them off first
      const st = picks.map(p => BY_ID.get(p.id)).find(s => s && !fits(s, b.dataset.z));
      if (st) {
        note(f(T.min_block || "Your sheet has {section} stickers, which don't come in {size}. Remove them first.",
               { section: secName(st.s), size: zName(b.dataset.z) }), true);
        return;
      }
      setSize(b.dataset.z);
      note("");
      layout();                            // the stickers move to the new grid at the new size (nd-builder.js glides them)
    });

    /* ---------- sheet cards: prices from the product's variants ---------- */
    root.querySelectorAll("[data-price]").forEach(el => {
      const p = data.prices && data.prices[el.dataset.price];
      el.textContent = p != null ? money(p, data.money_format) : "";
    });

    /* ---------- category chips ----------
       Pinned sections come first, in the order of the section's "Pinned section" blocks (theme
       editor), each with a small round face of its character (Hassan & Rayyan, Fattoum). The other
       sections follow in catalogue order. A section only gets a chip when it has stickers (in big-sticker
       mode: designs allowed big), so the row is drawn again when the mode changes. */
    let faces = new Map();
    const face = s => faces.get(s)
      ? `<img class="ssb-chip-face" src="${esc(faces.get(s))}" alt="" width="26" height="26" loading="lazy" onerror="this.remove()">` : "";
    function drawChips() {
      const pool = mode === "big" ? STICKERS.filter(s => s.b) : STICKERS, counts = {};
      pool.forEach(s => counts[s.s] = (counts[s.s] || 0) + 1);
      const pinned = (data.first || []).filter(p => p && counts[p.s]);
      faces = new Map(pinned.map(p => [p.s, p.face || ""]));
      const rank = new Map((NTDc().cats || []).map(c => [c.k, c.p]));
      const rest = Object.keys(counts).filter(s => !faces.has(s));
      if (rank.size) rest.sort((a, b) => (rank.get(a) || 999) - (rank.get(b) || 999));
      const order = pinned.map(p => p.s).concat(rest);
      const nNew = pool.filter(isNewS).length;
      if (section && !counts[section] && !(section === "__new" && nNew)) section = "";
      $("[data-chips]").innerHTML =
        [`<button type="button" class="ssb-chip" data-sec="" aria-pressed="${!section}">${esc(T.all || "All")} <span>${pool.length.toLocaleString("en")}</span></button>`]
        .concat(nNew ? [`<button type="button" class="ssb-chip ssb-chip--new" data-sec="__new" aria-pressed="${section === "__new"}">${esc(NTDc().newWord || "New")} <span>${nNew.toLocaleString("en")}</span></button>`] : [])
        .concat(order.map(s =>
          `<button type="button" class="ssb-chip${faces.get(s) ? " has-face" : ""}" data-sec="${esc(s)}" aria-pressed="${s === section}">${face(s)}${esc(secName(s))} <span>${counts[s].toLocaleString("en")}</span></button>`))
        .join("");
    }
    drawChips();
    $("[data-chips]").addEventListener("click", e => {
      const b = e.target.closest(".ssb-chip"); if (!b) return;
      section = b.dataset.sec;
      root.querySelectorAll(".ssb-chip").forEach(c => c.setAttribute("aria-pressed", c === b));
      renderLibrary();
    });

    $("[data-q]").addEventListener("input", e => { setQuery(e.target.value.trim().toLowerCase()); renderLibrary(); });

    /* ---------- library ---------- */
    // every word must be in the name or the section (English or its translated name); on the Arabic
    // site a word also counts when its English match is there (قطة → cat, nd-i18n-ar.js)
    let groups = [];
    const setQuery = q => { query = q; groups = q ? q.split(/\s+/).filter(Boolean).map(words) : []; };
    const match = s =>
      (mode !== "big" || s.b) &&
      (section === "__new" ? isNewS(s) : (!section || s.s === section)) &&
      (!groups.length || groups.every(g => {
        const text = (s.n + " " + s.s + " " + secName(s.s) + " " + (s.k || "") + " " + s.id).toLowerCase();
        return g.some(w => text.includes(w));
      }));
    const countOf = id => picks.reduce((n, p) => n + (p.id === id ? 1 : 0), 0);

    // Thousands of stickers: draw the matches 120 at a time, the next page when the end of the
    // grid comes within 800 px of the screen (or on "Show more" if that doesn't fire).
    const PAGE = 120;
    let shown = [], drawn = 0;
    const more = document.createElement("button");
    more.type = "button";
    more.className = "ssb-more";
    more.hidden = true;
    grid.after(more);
    more.addEventListener("click", () => drawMore());
    const io = "IntersectionObserver" in window ? new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting) || drawn >= shown.length) return;
      drawMore();
      io.unobserve(more); io.observe(more);   // re-check: still in view after this page? draw again
    }, { rootMargin: "800px 0px" }) : null;
    if (io) io.observe(more);

    // Each tile shows how many of that sticker are on the sheet (the badge, as on the home page). A sticker that
    // doesn't come in the sheet's size is greyed out ("is-off"); its tooltip and a tap say why.
    const isOff = s => mode !== "big" && !fits(s);
    const tileTitle = s => isOff(s) ? minNote(s)
      : f(mode === "big" ? (T.big_tile_title || "{name} — tap to choose a size") : (T.tile_title || "{name} — tap to add"), { name: s.n });
    const tileHTML = s => `
        <div class="ssb-item">
          <button type="button" class="ssb-tile${isOff(s) ? " is-off" : ""}" data-id="${s.id}" aria-pressed="${countOf(s.id) > 0}"${isOff(s) ? ' aria-disabled="true"' : ""}
                  style="--ssb-tile-bg:${tileColour(s.id)}" title="${esc(tileTitle(s))}">
            <img src="${esc(s.u)}" alt="${esc(s.n)}" loading="lazy" width="200" height="200">
            <span class="ssb-n" aria-hidden="true">${countOf(s.id) || ""}</span>${isNewS(s) ? `<span class="ssb-new">${esc(NTDc().newWord || "New")}</span>` : ""}
          </button>
          <p class="ssb-cap" lang="en" dir="ltr">${esc(s.n)}${STAFF ? `<br><small class="ssb-no">No. ${esc(s.id)}</small>` : ""}</p>
        </div>`;
    function offTile(t) {
      const s = BY_ID.get(idOf(t.dataset.id)), off = !!s && isOff(s);
      t.classList.toggle("is-off", off);
      if (off) t.setAttribute("aria-disabled", "true"); else t.removeAttribute("aria-disabled");
      if (s) t.title = tileTitle(s);
    }
    const syncTile = id => {
      const t = grid.querySelector(`.ssb-tile[data-id="${id}"]`);
      if (!t) return;
      t.setAttribute("aria-pressed", countOf(id) > 0);
      t.querySelector(".ssb-n").textContent = countOf(id) || "";
    };

    function drawMore() {
      const next = shown.slice(drawn, drawn + PAGE);
      grid.insertAdjacentHTML("beforeend", next.map(tileHTML).join(""));
      drawn += next.length;
      more.hidden = drawn >= shown.length;
      more.textContent = f(T.show_more || "Show more stickers ({n} left)", { n: shown.length - drawn });
    }

    function renderLibrary() {
      shown = STICKERS.filter(match);
      $("[data-count]").textContent = f(shown.length === 1 ? (T.count_one || "1 sticker") : (T.count || "{n} stickers"), { n: shown.length }) +
        (section ? f(T.count_in || " in {section}", { section: secName(section) }) : "") +
        (query ? f(T.count_match || " matching “{q}”", { q: query }) : "");
      $("[data-empty]").hidden = shown.length > 0;
      grid.innerHTML = "";
      drawn = 0;
      drawMore();
      if (io) { io.unobserve(more); io.observe(more); }
      else while (drawn < shown.length) drawMore();
    }

    grid.addEventListener("click", e => {
      const b = e.target.closest(".ssb-tile"); if (!b) return;
      if (mode === "big") { openBig(BY_ID.get(idOf(b.dataset.id))); return; }
      const id = idOf(b.dataset.id), st = BY_ID.get(id);
      if (!st) return;
      if (!fits(st)) { note(minNote(st), true); return; }
      if (picks.length >= maxOf()) {
        note(f(T.full_size || "Your {sheet} sheet is full — {max} {size} stickers.", { sheet, max: maxOf(), size: zName(size) }) + " " +
          (sheet === "A4" ? f(T.full_a4 || "Switch to A3 for {max3}, or tap one on the sheet to remove it.", { max3: maxOf("A3") })
                          : (T.full_a3 || "Tap one on the sheet to remove it.")) +
          smallerHint(), true);
        return;
      }
      picks.push({ uid: uidSeq++, id });
      syncTile(id);
      note("");
      layout();
    });

    /* ---------- sheet ---------- */
    $("[data-sizes]").addEventListener("click", e => {
      const b = e.target.closest("[data-sheet]"); if (!b) return;
      root.querySelectorAll("[data-sheet]").forEach(c => c.setAttribute("aria-pressed", c === b));
      note("");
      if (b.dataset.sheet === "BIG") { setMode("big"); return; }
      sheet = b.dataset.sheet;
      setMode("sheet");
      layout();
    });

    /** Sheet (A4 / A3) or big-sticker mode: the library, its chips and the desk follow. */
    function setMode(m) {
      if (mode === m) return;
      mode = m;
      root.classList.toggle("is-big", m === "big");
      closeMenu();
      if (!guide.hidden) { guide.hidden = true; guideBtn.setAttribute("aria-expanded", "false"); }
      drawChips();
      renderLibrary();
      fit();
    }

    /** The sheet's spaces in mm — an even grid across the printable area, as many as the sheet takes at its
        size (gridOf); a last row that isn't full starts from the side the sheet fills from. */
    function spaces(k = sheet, z = size) {
      const S = SHEETS[k], g = gridOf(k, z);
      const W = S.w - 2 * MARGIN, H = S.h - 2 * MARGIN;
      const sw = (W - (g.cols - 1) * GAP) / g.cols;
      const sh = (H - (g.rows - 1) * GAP) / g.rows;
      const out = [];
      for (let i = 0; i < g.n; i++) {
        const r = Math.floor(i / g.cols), c = i % g.cols;
        out.push({ x: MARGIN + c * (sw + GAP), y: MARGIN + r * (sh + GAP), w: sw, h: sh });
      }
      return out;
    }

    /** Each pick goes in the next space at the sheet's size: the longest side = S, M or L, the other
        side from its shape (r = width / height), never more than the space holds. */
    function pack() {
      const sp = spaces(), placed = [], L = SIZE_MM[size];
      let total = 0;
      for (const p of picks) {
        const st = BY_ID.get(p.id);
        if (!st) continue;
        total++;
        if (placed.length >= sp.length) continue;
        const s = sp[placed.length], r = st.r || 1;
        const w = r >= 1 ? L : L * r, h = r >= 1 ? L / r : L;
        const k = Math.min(1, s.w / w, s.h / h);
        placed.push({ ...p, st, w: w * k, h: h * k });
      }
      return { placed, spaces: sp, over: total - placed.length };
    }

    function layout() {
      closeMenu();                        // the spaces are drawn again: a menu would point at nothing
      const S = SHEETS[sheet];
      const { placed, spaces: sp, over } = pack();
      const max = sp.length, n = placed.length;
      const pct = (v, of) => (v / of * 100) + "%";

      paper.style.aspectRatio = `${S.w} / ${S.h}`;
      $(".ssb-marg").style.inset = `${pct(MARGIN, S.h)} ${pct(MARGIN, S.w)}`;
      $("[data-ruler]").style.width = pct(50, S.w);        // 5 cm, to scale

      paper.querySelectorAll(".ssb-cell, .ssb-slot").forEach(el => el.remove());
      const frag = document.createDocumentFragment();
      sp.forEach((s, i) => {
        const b = placed[i];
        const d = document.createElement(b ? "button" : "div");
        d.className = b ? "ssb-cell" : "ssb-slot";
        d.style[RTL ? "right" : "left"] = pct(s.x, S.w);  d.style.top = pct(s.y, S.h);   // the sheet fills from the right in Arabic
        d.style.width = pct(s.w, S.w); d.style.height = pct(s.h, S.h);
        if (b) {
          d.type = "button";
          d.dataset.uid = b.uid;
          d.title = f(T.cell_title || "{name} · {w} × {h} cm — tap to remove it", { name: b.st.n, w: cm(b.w), h: cm(b.h) });
          d.setAttribute("aria-label", f(T.cell_label || "{name} — tap to remove it", { name: b.st.n }));
          d.setAttribute("aria-controls", menu.id);
          d.setAttribute("aria-expanded", "false");
          // the sticker at its real size (the 5 cm ruler's scale), centred in its space
          d.innerHTML = `<img src="${esc(b.st.u)}" alt="" style="width:${pct(b.w, s.w)};height:${pct(b.h, s.h)}">`;
        }
        frag.appendChild(d);
      });
      paper.appendChild(frag);

      $("[data-paper-empty]").hidden = n > 0;
      $("[data-fill-label]").textContent = f(T.fill || "{n} of {max} stickers", { n, max });
      $("[data-fill-left]").textContent = n >= max ? (T.full_word || "Full")
        : f(max - n === 1 ? (T.left_one || "1 space left") : (T.left || "{n} spaces left"), { n: max - n });
      $("[data-bar]").style.width = pct(n, max);
      $("[data-meter]").classList.toggle("is-over", over > 0);

      const warn = $("[data-warn]");
      if (over > 0) {
        warn.hidden = false;
        warn.innerHTML = `<b>${esc(f(over === 1 ? (T.over_one || "1 sticker doesn't fit.") : (T.over || "{n} stickers don't fit."), { n: over }))}</b> ` +
          esc(f(sheet === "A4" ? (T.over_a4 || "An A4 sheet takes {max} {size} stickers. Remove {n}, or switch to A3 for {max3}.")
                               : (T.over_a3 || "An A3 sheet takes {max} {size} stickers. Remove {n}."),
                { max, n: over, max3: maxOf("A3"), size: zName(size) }) + smallerHint());
      } else {
        warn.hidden = true;               // a full sheet needs no box: the count already says "Full"
      }

      // the A4 / A3 cards: how many each takes at this size
      root.querySelectorAll("[data-cap]").forEach(el => { el.textContent = maxOf(el.dataset.cap); });
      $("[data-fill-mini]").textContent = f(T.mini || "{sheet} {size} · {n} of {max} stickers", { sheet, size: zName(size), n, max });
      const price = data.prices && data.prices[sheet];
      $("[data-total]").textContent = money(price, data.money_format);
      const ready = !!(data.variants && data.variants[sheet]);
      $("[data-add]").disabled = n === 0 || over > 0 || !ready;
      if (!ready) note(f(T.not_ready || "The {sheet} sheet isn't set up in the shop yet.", { sheet }), true);
      fit();
      drawGuide();
    }

    /* ---------- tap a sticker on the sheet: its name · Remove ----------
       A small menu under the sticker (above it in the lower half of the sheet), its arrow pointing at it.
       (The size is the whole sheet's: the switch on the desk.) A tap elsewhere or Escape closes it. */
    const menu = $("[data-menu]"), menuBox = menu.parentElement;
    let menuUid = 0;
    const cellOf = uid => paper.querySelector(`.ssb-cell[data-uid="${uid}"]`);

    function openMenu(uid) {
      const p = picks.find(q => q.uid === uid), cell = cellOf(uid);
      if (!p || !cell) return;
      menuUid = uid;
      $("[data-menu-name]").textContent = BY_ID.get(p.id).n;
      menu.hidden = false;
      placeMenu();
      cell.classList.add("is-open");
      cell.setAttribute("aria-expanded", "true");
      menu.querySelector("[data-menu-remove]").focus({ preventScroll: true });
    }

    function placeMenu() {
      const cell = cellOf(menuUid);
      if (menu.hidden || !cell) return;
      const box = menuBox.getBoundingClientRect(), r = cell.getBoundingClientRect(), pr = paper.getBoundingClientRect();
      const ox = box.left + menuBox.clientLeft, oy = box.top + menuBox.clientTop;
      const mw = menu.offsetWidth, mh = menu.offsetHeight, mid = r.left + r.width / 2;
      const below = r.top + r.height / 2 < pr.top + pr.height / 2;
      const x = Math.max(8, Math.min(mid - ox - mw / 2, menuBox.clientWidth - mw - 8));
      menu.style.left = `${x}px`;
      menu.style.top = `${below ? r.bottom - oy + 9 : r.top - oy - mh - 9}px`;
      menu.style.setProperty("--ssb-caret", `${mid - ox - x}px`);
      menu.classList.toggle("is-above", !below);
    }

    function closeMenu(refocus) {
      if (menu.hidden) return;
      menu.hidden = true;
      const cell = cellOf(menuUid);
      if (cell) {
        cell.classList.remove("is-open");
        cell.setAttribute("aria-expanded", "false");
        if (refocus) cell.focus({ preventScroll: true });
      }
      menuUid = 0;
    }

    paper.addEventListener("click", e => {
      const c = e.target.closest(".ssb-cell"); if (!c) return;
      const uid = +c.dataset.uid, again = !menu.hidden && menuUid === uid;
      closeMenu();
      if (!again) openMenu(uid);
    });

    menu.addEventListener("click", e => {
      const p = picks.find(q => q.uid === menuUid);
      if (!p || !e.target.closest("[data-menu-remove]")) return;
      const at = picks.indexOf(p);
      picks.splice(at, 1);
      layout();                           // (layout closes the menu)
      syncTile(p.id);
      const left = paper.querySelectorAll(".ssb-cell");
      if (left.length) left[Math.min(at, left.length - 1)].focus({ preventScroll: true });
    });

    document.addEventListener("click", e => {
      if (!menu.hidden && !menu.contains(e.target) && !(e.target.closest && e.target.closest(".ssb-cell"))) closeMenu();
    });
    root.addEventListener("keydown", e => { if (e.key === "Escape" && !menu.hidden) closeMenu(true); });

    /* ---------- size guide: S, M and L side by side at the sheet's own scale ----------
       Drawn with the latest sticker on the sheet (Scooter cat while the sheet is empty), as big as it
       would be on the sheet preview, with the same 5 cm ruler, and how many of each fit on the sheet.
       The sheet's size is marked. Open/closed by the "Size guide" link. */
    const guide = $("[data-guide]"), guideBtn = $("[data-guide-open]");
    function drawGuide() {
      if (guide.hidden) return;
      const px = paper.getBoundingClientRect().width / SHEETS[sheet].w;      // px per mm, as on the sheet
      const st = (picks.length && BY_ID.get(picks[picks.length - 1].id)) || BY_ID.get(2125) || STICKERS[0];
      if (!st) return;
      const r = st.r || 1;
      guide.querySelectorAll("[data-gz]").forEach(fig => {
        const z = fig.dataset.gz, L = SIZE_MM[z], img = fig.querySelector("img");
        if (img.getAttribute("src") !== st.u) img.src = st.u;
        img.style.width = `${(r >= 1 ? L : L * r) * px}px`;
        img.style.height = `${(r >= 1 ? L / r : L) * px}px`;
        fig.classList.toggle("is-on", z === size);
        const n = fig.querySelector("[data-gn]");
        if (n) n.textContent = f(T.per_sheet || "{n} on {sheet}", { n: maxOf(sheet, z), sheet });
      });
      guide.querySelector("[data-guide-ruler]").style.width = `${50 * px}px`;
      guide.querySelectorAll("[data-guide-sheet]").forEach(el => { el.textContent = sheet; });
    }
    guideBtn.addEventListener("click", () => {
      guide.hidden = !guide.hidden;
      guideBtn.setAttribute("aria-expanded", !guide.hidden);
      drawGuide();
    });
    $("[data-guide-close]").addEventListener("click", () => {
      guide.hidden = true;
      guideBtn.setAttribute("aria-expanded", "false");
      guideBtn.focus();
    });

    $("[data-clear]").addEventListener("click", () => {
      picks = [];
      grid.querySelectorAll('.ssb-tile[aria-pressed="true"]').forEach(t => syncTile(+t.dataset.id));
      note("");
      layout();
    });

    /* ---------- big stickers: the size panel ----------
       A dialog with the design, its sizes (the Big Sticker product's variants: name, price, what it suits —
       rendered by the section), the drawing on a laptop and a suitcase at the chosen size, a quantity and
       Add to cart. Each design is its own cart line: Design (visible) and _Design ID (hidden); the same design
       at the same size again just adds to that line's quantity. */
    const bigD = $("[data-bigd]");
    let bigCur = null;
    const sizeKey = t => String(t).replace(/\s+/g, "").replace(/[×*X]/g, "x").toLowerCase();     // "13 × 19" → "13x19"
    // a size the design may print at: no bigger than its bmax, if it has one (compared side for side, long with long)
    const sizeOk = (st, v) => {
      const m = st.bmax && NDBig.parse(st.bmax);
      return !m || (Math.max(v.size.w, v.size.h) <= Math.max(m.w, m.h) && Math.min(v.size.w, v.size.h) <= Math.min(m.w, m.h));
    };

    function openBig(st, want) {
      if (!bigD || !BIG.length || !st || !st.b || !window.NDBig) return;
      const ok = v => v && sizeOk(st, v);
      const asked = want && BIG.find(x => sizeKey(x.title) === sizeKey(want));
      const v = [asked, bigCur && bigCur.v, BIG[Math.min(1, BIG.length - 1)]].find(ok) || BIG.filter(ok).pop();
      if (!v) return;
      bigCur = { st, v };
      bigD.querySelector("[data-bigd-img]").src = st.u;
      bigD.querySelector("[data-bigd-name]").textContent = st.n;
      bigD.querySelector("[data-bigd-qty]").value = 1;
      bigD.querySelector("[data-bigd-msg]").textContent = "";
      drawBig();
      if (!bigD.open) { if (bigD.showModal) bigD.showModal(); else bigD.setAttribute("open", ""); }
    }

    function drawBig() {
      const { st, v } = bigCur;
      bigD.querySelector("[data-bigd-scene]").innerHTML = NDBig.scene({ size: v.size, img: st.u, r: st.r, t: BIG_T });
      bigD.querySelectorAll("[data-bv]").forEach(b => {
        const bv = BIG.find(x => x.id === +b.dataset.bv);
        b.setAttribute("aria-pressed", +b.dataset.bv === v.id);
        b.disabled = !bv || !bv.available || !sizeOk(st, bv);                // sold out, or bigger than this design allows
      });
      const add = bigD.querySelector("[data-bigd-add]");
      add.disabled = !v.available;
      add.textContent = v.available ? (T.add || "Add to cart") : (T.sold_out || "Sold out");
    }

    function closeBig() {
      if (bigD && bigD.open) { if (bigD.close) bigD.close(); else bigD.removeAttribute("open"); }
    }

    async function addBig() {
      const { st, v } = bigCur, btn = bigD.querySelector("[data-bigd-add]"), msg = bigD.querySelector("[data-bigd-msg]");
      const quantity = Math.max(1, parseInt(bigD.querySelector("[data-bigd-qty]").value, 10) || 1);
      const properties = { Design: st.n, "_Design ID": String(st.id) };
      if (st.own) properties["_Design picture"] = st.u;           // stickers the shop added itself (Metaobjects)
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      msg.classList.remove("is-error");
      try {
        const r = await fetch(rootUrl + "cart/add.js", {
          method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: v.id, quantity, properties }] }),
        });
        const res = await r.json();
        if (!r.ok || res.status) throw new Error(res.description || res.message || "");
        const cartNow = await (await fetch(rootUrl + "cart.js")).json();
        document.querySelectorAll("[data-cart-count]").forEach(el => { el.textContent = cartNow.item_count; });
        const icon = document.querySelector("[data-cart-icon]");
        if (icon && icon.animate) icon.animate([{ scale: 1 }, { scale: 1.3 }, { scale: 1 }], { duration: 440, easing: "cubic-bezier(.34,1.56,.64,1)" });
        msg.innerHTML = esc(f(T.big_added || "Added — “{name}”, {size} cm.", { name: st.n, size: v.title })) + " " +
          f(T.in_cart || 'In your cart — <a href="{url}">view cart</a>', { url: rootUrl + "cart" });
      } catch (err) {
        msg.textContent = err.message || T.add_fail_item || "Couldn't add it — please try again.";
        msg.classList.add("is-error");
      } finally {
        btn.disabled = !v.available;
        btn.removeAttribute("aria-busy");
      }
    }

    if (bigD) {
      bigD.addEventListener("click", e => {
        const sb = e.target.closest("[data-bv]");
        if (sb) { const v = BIG.find(x => x.id === +sb.dataset.bv); if (v) { bigCur.v = v; drawBig(); } return; }
        const step = e.target.closest("[data-bigd-step]");
        if (step) {
          const q = bigD.querySelector("[data-bigd-qty]");
          q.value = Math.max(1, (parseInt(q.value, 10) || 1) + parseInt(step.dataset.bigdStep, 10));
          return;
        }
        if (e.target.closest("[data-bigd-add]")) { addBig(); return; }
        if (e.target.closest("[data-bigd-close]") || e.target === bigD) closeBig();     // the ✕, or a tap outside the panel
      });
    }

    /* ---------- keep Add to cart on screen ----------
       Wide screens: the card is sticky and fit() shrinks the sheet preview until the whole card,
       Add to cart included, fits in the window. Phones, tablets and very short windows get
       "is-compact" instead: the Total + Add to cart row pins to the bottom of the screen. */
    const side = $(".ssb-side"), card = $(".ssb-card"), wrap = $(".ssb-paper-wrap");
    const narrow = window.matchMedia("(max-width: 989px)");
    const MIN_SHEET = 220;                 // px tall — below this the preview gets too small to read

    // Bars fixed over the bottom of the window: Shopify's theme-preview bar and ToyTime's mobile
    // toolbar. The pinned row sits above them, and the sticky card keeps clear of them.
    function bottomInset() {
      let inset = 0;
      document.querySelectorAll("#PBarNextFrameWrapper, .mobile-toolbar__icons").forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.height > 0 && r.top < innerHeight && r.bottom >= innerHeight - 2) inset = Math.max(inset, innerHeight - r.top);
      });
      return Math.round(inset);
    }

    function fit() {
      const inset = bottomInset();
      root.style.setProperty("--ssb-inset", `${inset}px`);
      root.classList.remove("is-compact");
      wrap.style.maxWidth = "";
      if (mode === "big") return;              // no sheet and no Add to cart row on the desk: nothing to fit
      if (narrow.matches) { root.classList.add("is-compact"); return; }
      const S = SHEETS[sheet];
      const top = parseFloat(getComputedStyle(side).top) || 0;
      const room = innerHeight - inset - top - 16 - (card.offsetHeight - paper.offsetHeight);
      if (room < MIN_SHEET) { root.classList.add("is-compact"); return; }
      if (room < paper.offsetHeight) wrap.style.maxWidth = `${Math.floor(room * S.w / S.h)}px`;
    }

    let fitQueued = false;
    const queueFit = () => {
      if (fitQueued) return;
      fitQueued = true;
      requestAnimationFrame(() => { fitQueued = false; fit(); placeMenu(); drawGuide(); });
    };
    window.addEventListener("resize", queueFit);
    window.addEventListener("load", queueFit);

    $("[data-view]").addEventListener("click", () => card.scrollIntoView({ behavior: "smooth", block: "start" }));

    /* ---------- add to cart ---------- */
    let noteTimer = 0;
    function note(msg, isError) {
      const m = $("[data-msg]");
      m.textContent = msg || "";
      m.classList.toggle("is-error", !!isError);
      clearTimeout(noteTimer);
      if (msg) noteTimer = setTimeout(() => { m.textContent = ""; }, isError ? 6000 : 4000);
    }

    $("[data-add]").addEventListener("click", async () => {
      const { placed, over } = pack();
      const variant = data.variants && data.variants[sheet];
      if (!placed.length || over > 0 || !variant) return;

      const btn = $("[data-add]");
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      const cart = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
      const fd = new FormData();
      fd.append("id", variant);
      fd.append("quantity", "1");
      // the sheet's one size in the Sheet line: "A4 · L (3.5 cm) · 12 stickers"
      fd.append("properties[Sheet]", `${sheet} · ${size} (${cm(SIZE_MM[size])} cm) · ${placed.length} sticker${placed.length === 1 ? "" : "s"}`);
      // " | " between names: some sticker names contain commas ("Bend, don't break"). Each name still carries
      // the size, "Black cat sitting (L)", and _Sizes lists it for each sticker in the same order as _Sticker IDs
      // (L,L,L…) — the order looks as it did when sizes could differ, so the print team reads it the same way.
      fd.append("properties[Stickers]", placed.map(p => `${p.st.n} (${size})`).join(" | "));
      fd.append("properties[_Sticker IDs]", placed.map(p => p.st.id).join(","));
      fd.append("properties[_Sizes]", placed.map(() => size).join(","));
      const own = placed.filter(p => p.st.own);
      if (own.length) fd.append("properties[_Sticker pictures]",
        [...new Set(own.map(p => p.st.id + "=" + p.st.u))].join(" "));
      if (cart && typeof cart.getSectionsToRender === "function") {
        fd.append("sections", cart.getSectionsToRender().map(s => s.id));
        fd.append("sections_url", window.location.pathname);
      }

      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const addUrl = (window.routes && window.routes.cart_add_url) || rootUrl + "cart/add";
      try {
        const r = await fetch(addUrl, {
          method: "POST",
          headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/javascript" },
          body: fd,
        });
        const res = await r.json();
        if (res.status) throw new Error(res.description || res.message || T.add_fail || "Couldn't add the sheet to the cart.");
        note(f(placed.length === 1 ? (T.added_sheet_one || "Added — {sheet} sheet with 1 sticker.")
                                   : (T.added_sheet || "Added — {sheet} sheet with {n} stickers."), { sheet, n: placed.length }));
        if (cart && typeof cart.renderContents === "function") {
          cart.classList.remove("is-empty");
          cart.renderContents(res);
        } else {
          window.location.href = rootUrl + "cart";
        }
      } catch (err) {
        note(err.message || T.add_fail || "Couldn't add the sheet to the cart.", true);
      } finally {
        btn.removeAttribute("aria-busy");
        layout();
      }
    });

    // Links from the home page: ?section=Cats · ?q=karak · ?sheet=A3 · ?size=M (the sheet's sticker size) ·
    // ?picks=2125,2755 (put on the sheet) or with sizes ?picks=2125:L,2755:S (the sheet takes one size: sheetSize)
    // A section matches by its name with or without spaces, "&" or dashes: ?section=Fattoum,
    // ?section=Hassan%20%26%20Rayyan and ?section=hassan-rayyan all open the same chip.
    const qs = new URLSearchParams(location.search);
    if (qs.get("sheet") === "A3") {
      sheet = "A3";
      root.querySelectorAll("[data-sheet]").forEach(c => c.setAttribute("aria-pressed", c.dataset.sheet === "A3"));
    }
    const askedSize = String(qs.get("size") || "").toUpperCase();
    if (SIZES.includes(askedSize)) setSize(askedSize);

    /* The one size for stickers that arrive together (?picks, a ready sheet's preset), which may carry sizes
       ("2125:L"): ?size= if the link says, else the size most of them carry (a tie goes to the bigger), else the
       sheet's size — and if they don't all fit at that size, the biggest size that takes them all. Never smaller
       than one of them comes in (Hassan & Rayyan, Fattoum: M). */
    function sheetSize(items) {
      const lo = items.reduce((m, p) => Math.max(m, rank(minOf(BY_ID.get(p.id)))), 0);
      if (SIZES.includes(askedSize)) return SIZES[Math.max(rank(askedSize), lo)];
      const n = {};
      items.forEach(p => { if (SIZES.includes(p.z)) n[p.z] = (n[p.z] || 0) + 1; });
      let z = SIZES.filter(s => n[s]).sort((a, b) => n[b] - n[a] || rank(b) - rank(a))[0] || size;
      if (rank(z) < lo) z = SIZES[lo];
      if (items.length > maxOf(sheet, z)) {
        const roomy = SIZES.filter(s => rank(s) >= lo && items.length <= maxOf(sheet, s));
        if (roomy.length) z = roomy[roomy.length - 1];
      }
      return z;
    }
    const plain = t => String(t).toLowerCase().replace(/[^a-z0-9]+/g, "");
    const wantSec = plain(qs.get("section") || "");
    if (wantSec) {
      const chip = [...root.querySelectorAll(".ssb-chip")].find(c => c.dataset.sec && plain(c.dataset.sec) === wantSec);
      if (chip) { section = chip.dataset.sec; root.querySelectorAll(".ssb-chip").forEach(c => c.setAttribute("aria-pressed", c === chip)); }
    }
    if (qs.get("q")) { setQuery(qs.get("q").trim().toLowerCase()); $("[data-q]").value = qs.get("q"); }
    const linked = (qs.get("picks") || "").split(",").filter(Boolean).map(v => v.split(":"))
      .map(([v, z]) => ({ id: idOf(v), z: String(z || "").toUpperCase() })).filter(p => BY_ID.has(p.id));
    if (linked.length) {
      setSize(sheetSize(linked));
      linked.slice(0, maxOf()).forEach(p => picks.push({ uid: uidSeq++, id: p.id }));
    }

    renderLibrary();
    layout();

    // Big stickers from the Laptop & Travel page: ?mode=big (or ?sheet=big) opens the big-sticker mode,
    // &design=<id> opens that design's size panel (designs allowed big only), &size=19x27 picks the size.
    if (BIG.length && (qs.get("mode") === "big" || /^big$/i.test(qs.get("sheet") || ""))) {
      const bigCard = root.querySelector('[data-sheet="BIG"]');
      if (bigCard) {
        root.querySelectorAll("[data-sheet]").forEach(c => c.setAttribute("aria-pressed", c === bigCard));
        setMode("big");
        const want = qs.get("design");
        if (want && BY_ID.has(idOf(want))) openBig(BY_ID.get(idOf(want)), qs.get("size"));
      }
    }

    // "Customise this sheet" on a ready sheet: ?preset=<product handle> puts that sheet's stickers on, at one
    // size (sheetSize: the size most of them have on the ready sheet) — the product metafield builder.preset,
    // read through the product's ?view=preset template. Stickers that aren't in the builder are skipped; too
    // many for the sheet → the usual note.
    // (No "Accept: application/json" here: with it Shopify answers with its own product JSON instead.)
    const presetHandle = (qs.get("preset") || "").trim().toLowerCase();
    if (/^[a-z0-9][a-z0-9-]*$/.test(presetHandle)) {
      try {
        const res = await fetch(`${ND.root || ""}/products/${presetHandle}?view=preset`);
        const ps = JSON.parse(await res.text());
        if (!("preset" in ps)) throw new Error("no preset view");
        const items = String(ps.preset || "").split(",").map(v => v.trim()).filter(Boolean)
          .map(v => v.split(":")).map(([v, z]) => ({ id: idOf(v), z: String(z || "").toUpperCase() })).filter(p => BY_ID.has(p.id));
        if (items.length) {
          setSize(sheetSize(items));
          picks = items.map(p => ({ uid: uidSeq++, id: p.id }));
          grid.querySelectorAll(".ssb-tile").forEach(t => syncTile(idOf(t.dataset.id)));
          layout();
          note(f(T.preset_loaded || "Customising “{title}” — swap any sticker or size, then add it to your cart.", { title: ps.title || "" }));
        }
      } catch (err) {
        note(T.preset_failed || "That sheet's stickers didn't load — please try again.", true);
      }
    }
  }
})();
